import React, { forwardRef } from 'react';
import { InvoiceItem } from './InvoiceItemsTable';
import { InvoiceFormData } from './InvoiceForm';

interface InvoicePreviewProps {
  data: InvoiceFormData;
  items: InvoiceItem[];
}

export const InvoicePreview = forwardRef<HTMLDivElement, InvoicePreviewProps>(
  ({ data, items }, ref) => {
    const calculateTotal = () => {
      return items.reduce((sum, item) => sum + item.quantity * item.rate, 0);
    };

    return (
      <div
        ref={ref}
        className="bg-white p-8 rounded-lg shadow-lg"
        style={{ width: '210mm', minHeight: '297mm' }}
      >
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">INVOICE</h1>
          <div className="text-sm text-gray-600">
            <p>Invoice #: {data.meta.number}</p>
            <p>Date: {data.meta.date}</p>
          </div>
        </div>

        {/* Contractor and Client Info */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <h2 className="text-sm font-semibold text-gray-700 mb-2">FROM:</h2>
            <div className="text-sm text-gray-800">
              <p className="font-semibold">{data.contractor.name}</p>
              <p>{data.contractor.email}</p>
              <p className="whitespace-pre-line">{data.contractor.address}</p>
              {data.contractor.taxId && <p>Tax ID: {data.contractor.taxId}</p>}
            </div>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-700 mb-2">TO:</h2>
            <div className="text-sm text-gray-800">
              <p className="font-semibold">{data.client.name}</p>
              <p className="whitespace-pre-line">{data.client.address}</p>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <table className="w-full mb-8">
          <thead>
            <tr className="border-b-2 border-gray-300">
              <th className="text-left py-2 text-sm font-semibold text-gray-700">
                Description
              </th>
              <th className="text-right py-2 text-sm font-semibold text-gray-700 w-20">
                Qty
              </th>
              <th className="text-right py-2 text-sm font-semibold text-gray-700 w-28">
                Rate
              </th>
              <th className="text-right py-2 text-sm font-semibold text-gray-700 w-28">
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-gray-200">
                <td className="py-3 text-sm text-gray-800">{item.description}</td>
                <td className="py-3 text-sm text-gray-800 text-right">{item.quantity}</td>
                <td className="py-3 text-sm text-gray-800 text-right">
                  ${item.rate.toFixed(2)}
                </td>
                <td className="py-3 text-sm text-gray-800 text-right">
                  ${(item.quantity * item.rate).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-300">
              <td colSpan={3} className="py-3 text-right font-semibold text-gray-800">
                Total:
              </td>
              <td className="py-3 text-right font-bold text-lg text-gray-900">
                ${calculateTotal().toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>

        {/* Notes */}
        {data.notes && (
          <div className="mt-8 pt-4 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes:</h3>
            <p className="text-sm text-gray-600 whitespace-pre-line">{data.notes}</p>
          </div>
        )}
      </div>
    );
  }
);

InvoicePreview.displayName = 'InvoicePreview';
